import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Plus, Trophy, Users } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Select, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { ContestStatusBadge } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useCreateContest, useMyContests } from '@/features/contest/api'
import { useMarketplaceCategories } from '@/features/marketplace/api'
import { ApiError } from '@/lib/api'
import { formatDate, formatRupiah } from '@/lib/format'

export function MyContestsPage() {
  const { data, isLoading } = useMyContests()
  const [createOpen, setCreateOpen] = useState(false)
  const contests = data?.contests ?? []

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Sayembara saya</h1>
          <p className="mt-1 text-muted">
            Buka sayembara ketika Anda ingin membandingkan banyak gagasan sebelum memilih.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          Buat sayembara
        </Button>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : contests.length === 0 ? (
        <Card>
          <EmptyState illustration="box"
            icon={<Trophy className="size-6" />}
            title="Belum ada sayembara"
            description="Hadiah disetor ke escrow sebelum sayembara tayang, sehingga peserta tahu hadiahnya benar-benar ada."
            action={<Button onClick={() => setCreateOpen(true)}>Buat sayembara pertama</Button>}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {contests.map((contest) => (
            <Card key={contest.id}>
              <CardBody className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <ContestStatusBadge status={contest.status} />
                    <Badge tone="brand">{contest.category}</Badge>
                    {!contest.funded ? <Badge tone="warning">Hadiah belum disetor</Badge> : null}
                  </div>
                  <h2 className="mt-2 font-bold text-ink">{contest.title}</h2>
                  <p className="mt-1 flex flex-wrap gap-x-4 text-sm text-muted">
                    <span className="inline-flex items-center gap-1">
                      <Users className="size-3.5" />
                      {contest.entry_count} karya
                    </span>
                    {contest.closes_at ? <span>ditutup {formatDate(contest.closes_at)}</span> : null}
                  </p>
                </div>

                <div className="shrink-0 text-right">
                  <p className="text-lg font-extrabold tabular-nums text-ink">
                    {formatRupiah(contest.prize_amount)}
                  </p>
                  <Link to="/sayembara/$contestId" params={{ contestId: contest.id }} className="mt-3 block">
                    <Button size="sm">{contest.funded ? 'Kelola & nilai' : 'Setor hadiah'}</Button>
                  </Link>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <CreateContestDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}

function CreateContestDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createContest = useCreateContest()
  const { data: categories } = useMarketplaceCategories()
  const toast = useToast()

  const [values, setValues] = useState({
    title: '',
    description: '',
    category: '',
    prize_amount: 0,
    closes_at: '',
  })

  useEffect(() => {
    if (categories?.length && !values.category) {
      setValues((current) => ({ ...current, category: categories[0]?.slug ?? '' }))
    }
  }, [categories, values.category])

  const fee = Math.floor((values.prize_amount * 10) / 100)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Buat sayembara"
      description="Sayembara tayang setelah hadiahnya disetor ke rekening penampungan."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={createContest.isPending}
            disabled={values.title.trim().length < 8 || values.prize_amount <= 0}
            onClick={() =>
              createContest.mutate(
                {
                  title: values.title,
                  description: values.description,
                  category: values.category,
                  skills: [],
                  prize_amount: values.prize_amount,
                  closes_at: values.closes_at ? new Date(values.closes_at).toISOString() : undefined,
                },
                {
                  onSuccess: () => {
                    toast.success('Sayembara dibuat', 'Setor hadiah untuk menayangkannya.')
                    setValues({ title: '', description: '', category: values.category, prize_amount: 0, closes_at: '' })
                    onClose()
                  },
                  onError: (error) =>
                    toast.error('Gagal membuat sayembara', error instanceof ApiError ? error.message : undefined),
                },
              )
            }
          >
            Simpan draf
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Judul sayembara"
          required
          value={values.title}
          onChange={(event) => setValues((current) => ({ ...current, title: event.target.value }))}
          placeholder="Misalnya: Sayembara logo dan identitas visual"
        />

        <Textarea
          label="Uraian & kriteria penilaian"
          rows={6}
          hint="Minimal 80 karakter. Sebutkan kriteria penilaian sejelas mungkin agar karya yang masuk terarah."
          value={values.description}
          onChange={(event) => setValues((current) => ({ ...current, description: event.target.value }))}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Bidang"
            value={values.category}
            onChange={(event) => setValues((current) => ({ ...current, category: event.target.value }))}
          >
            {categories?.map((category) => (
              <option key={category.slug} value={category.slug}>
                {category.name}
              </option>
            ))}
          </Select>

          <Input
            label="Tenggat pengiriman"
            type="date"
            value={values.closes_at}
            onChange={(event) => setValues((current) => ({ ...current, closes_at: event.target.value }))}
          />
        </div>

        <Input
          label="Hadiah"
          type="number"
          min={0}
          step={500000}
          prefix="Rp"
          value={values.prize_amount || ''}
          onChange={(event) => setValues((current) => ({ ...current, prize_amount: Number(event.target.value) }))}
        />

        <div className="rounded-xl bg-sunken p-4">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted">Anda setor</dt>
              <dd className="font-semibold text-ink">{formatRupiah(values.prize_amount)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted">Komisi platform (10%)</dt>
              <dd className="font-semibold text-ink">{formatRupiah(fee)}</dd>
            </div>
            <div className="flex justify-between border-t border-line pt-2">
              <dt className="font-semibold text-ink">Diterima pemenang</dt>
              <dd className="font-bold text-success">{formatRupiah(values.prize_amount - fee)}</dd>
            </div>
          </dl>
          <p className="mt-3 text-xs leading-relaxed text-subtle">
            Komisi dipotong dari hadiah saat dibayarkan, bukan ditambahkan ke setoran Anda.
          </p>
        </div>
      </div>
    </Dialog>
  )
}
